import type { LucideIcon } from "lucide-react"
import { motion, useReducedMotion } from "motion/react"
import { useLayoutEffect, useRef, useState } from "react"

/**
 * Pestañas, con el patrón de WAI-ARIA entero.
 *
 * ⚠ **No son botones con aspecto de pestaña.** Eso significa `role="tablist"`,
 * `aria-selected`, `aria-controls`, y —la parte que casi siempre falta— **una sola
 * parada de tabulador para el grupo**: se entra con Tab y se cambia con las flechas,
 * como en cualquier pestaña del sistema operativo. Con botones normales hay que
 * tabular tantas veces como pestañas sólo para llegar al contenido.
 *
 * ⚠ **Son del CRISTAL, como el menú: la pista apenas aclara y la elegida sube la luz
 * con su filo** (`.activo-cristal`); el roce es `.toque-cristal` y el foco,
 * `.foco-cristal`. Fueron pestañas de carpeta —la barra en `--surface-elevated` y la
 * activa del color del lienzo, `--background`, tapando la línea de abajo— y eso sólo
 * funciona sobre un lienzo opaco. Desde que el área de trabajo es cristal, la barra
 * salía de un gris azulado y la activa como un rectángulo negro con borde sobre el
 * vino: dos parches de la paleta del tema, que es lo que prohíbe la regla del cristal
 * (`.claude/rules/frontend.md`).
 *
 * ⚠ **La lengüeta se posiciona MIDIENDO el botón activo, no con `layoutId`.**
 * `layoutId` es lo natural para esto y fue el primer intento, pero su proyección
 * necesita haber medido cada elemento antes de poder interpolar entre dos: recién
 * cargada la página la primera transición daba un tirón, y sólo después de haber
 * pasado por todas las pestañas se deslizaba. `layoutRoot` no lo arregló. Medir el
 * botón y animar hasta ahí no depende de qué se haya pintado antes, así que funciona
 * desde el primer cambio.
 *
 * ⚠ **El filo de la pista es una sombra interior y no un `border`.** `offsetLeft` se
 * mide desde el borde interior de la pista y `left: 0` también; con un borde de por
 * medio, la lengüeta quedaba corrida un píxel respecto al botón que marca.
 *
 * ⚠ **La pista desplaza en horizontal DENTRO de su caja.** En un teléfono, cinco
 * pestañas no caben en 320 px: se desliza la tira, nunca la página.
 *
 * La primera colocación es instantánea a propósito: al cargar, la lengüeta ya está
 * en su sitio en vez de viajar desde el origen.
 *
 * ⚠ **`useReducedMotion` además del reset de CSS**, y no es redundante: el reset de
 * `globales.css` anula transiciones declaradas en CSS, pero motion escribe estilos
 * en cada cuadro desde JavaScript y eso ninguna regla de CSS lo detiene.
 *
 * Quien la use se encarga de poner `role="tabpanel"`, `id` y `aria-labelledby` en el
 * contenido — sin eso, `aria-controls` apunta al vacío.
 */

export type Pestana<T extends string> = {
  valor: T
  etiqueta: string
  /**
   * Opcional, y decorativo: la etiqueta sigue siendo lo que se lee. Toma el color del
   * texto de su pestaña, así que la elegida lo enciende sin un color propio.
   */
  icono?: LucideIcon
}

type Props<T extends string> = {
  pestanas: readonly Pestana<T>[]
  activa: T
  onCambiar: (valor: T) => void
  /** Qué grupo de pestañas es, para quien navega por lista de elementos. */
  etiqueta: string
  /** `id` del elemento con `role="tabpanel"` que estas pestañas gobiernan. */
  idPanel: string
  /** Prefijo de los `id` de cada pestaña. El panel lo referencia con `aria-labelledby`. */
  prefijoId: string
}

export function Pestanas<T extends string>({
  pestanas,
  activa,
  onCambiar,
  etiqueta,
  idPanel,
  prefijoId,
}: Props<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const contenedor = useRef<HTMLDivElement | null>(null)
  const pista = useRef<HTMLDivElement | null>(null)
  const sinMovimiento = useReducedMotion()

  const [lengueta, setLengueta] = useState<{ x: number; ancho: number } | null>(null)
  const indice = pestanas.findIndex((p) => p.valor === activa)

  useLayoutEffect(() => {
    const medir = () => {
      const boton = refs.current[indice]
      if (!boton) return
      setLengueta({ x: boton.offsetLeft, ancho: boton.offsetWidth })
    }
    medir()

    // ⚠ **La elegida, a la vista.** En un teléfono la pista desliza, y sin esto se
    // entra por un enlace a la cuarta pestaña y la encendida queda fuera, a la
    // derecha: no se ve en qué vista se está. Sólo al cambiar de pestaña y no en cada
    // medida: si no, el tirón desharía lo que desplace quien la está recorriendo.
    const elegida = refs.current[indice]
    const caja = pista.current
    if (elegida && caja) {
      const margen = 16
      const inicio = elegida.offsetLeft - margen
      const fin = elegida.offsetLeft + elegida.offsetWidth + margen
      if (inicio < caja.scrollLeft) caja.scrollLeft = inicio
      else if (fin > caja.scrollLeft + caja.clientWidth) caja.scrollLeft = fin - caja.clientWidth
    }

    // Las etiquetas cambian de ancho cuando termina de cargar la tipografía y
    // cuando la ventana se estrecha. Sin volver a medir, la lengüeta se queda donde
    // el botón ESTABA.
    const observador = new ResizeObserver(medir)
    if (contenedor.current) observador.observe(contenedor.current)
    return () => observador.disconnect()
  }, [indice])

  /** Flechas, Inicio y Fin mueven el foco Y la selección, como manda el patrón. */
  const teclado = (e: React.KeyboardEvent, desde: number) => {
    const saltos: Record<string, number> = {
      ArrowRight: desde + 1,
      ArrowLeft: desde - 1,
      Home: 0,
      End: pestanas.length - 1,
    }
    const destino = saltos[e.key]
    if (destino === undefined) return
    e.preventDefault()
    // Da la vuelta: desde la última, la derecha lleva a la primera.
    const i = (destino + pestanas.length) % pestanas.length
    const siguiente = pestanas[i]
    if (siguiente === undefined) return
    onCambiar(siguiente.valor)
    refs.current[i]?.focus()
  }

  return (
    <div ref={pista} className="mb-4 max-w-full overflow-x-auto">
      <div
        ref={contenedor}
        role="tablist"
        aria-label={etiqueta}
        className="relative inline-flex gap-1 rounded-[calc(var(--radius-control)+4px)] p-1"
        style={{
          backgroundColor: "rgb(var(--marco-tinta) / 0.05)",
          boxShadow: "inset 0 0 0 1px rgb(var(--marco-tinta) / 0.1)",
        }}
      >
        {lengueta !== null && (
          <motion.span
            aria-hidden={true}
            className="activo-cristal absolute top-1 bottom-1 left-0 rounded-[var(--radius-control)]"
            // ⚠ **Se anima `x` y el ancho se fija al instante (12/10/2026).** Animaba
            // `width`, que recalcula la maquetación en cada fotograma: la regla es sólo
            // `transform`/`opacity`. Se probó `layout` (que lo hace con transformaciones)
            // y retenía la salida de los modales de `AnimatePresence` en la misma
            // pantalla: el diálogo de «Nueva tarea» llegaba a opacidad 0 y no se iba.
            // Sin animación de entrada: al cargar ya está colocada.
            initial={false}
            style={{ width: lengueta.ancho }}
            animate={{ x: lengueta.x }}
            transition={
              sinMovimiento
                ? { duration: 0 }
                : // Un muelle, no una duración fija: el recorrido entre dos pestañas
                  // contiguas y entre las de los extremos no debe durar lo mismo.
                  { type: "spring", stiffness: 420, damping: 34 }
            }
          />
        )}

        {pestanas.map((p, i) => {
          const seleccionada = p.valor === activa
          return (
            <button
              key={p.valor}
              ref={(el) => {
                refs.current[i] = el
              }}
              type="button"
              role="tab"
              id={`${prefijoId}-${p.valor}`}
              aria-selected={seleccionada}
              aria-controls={idPanel}
              // Una sola parada de tabulador para todas: la activa.
              tabIndex={seleccionada ? 0 : -1}
              onClick={() => onCambiar(p.valor)}
              onKeyDown={(e) => teclado(e, i)}
              // `relative` para quedar por encima de la lengüeta, que es absoluta y va
              // antes en el orden. El peso es el mismo en todas: si la elegida fuera
              // más gruesa, cada cambio movería las etiquetas de al lado.
              className={`foco-cristal relative inline-flex items-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] px-3.5 py-2 text-sm font-medium transition-colors ${
                seleccionada
                  ? "text-[rgb(var(--marco-tinta)/1)]"
                  : "toque-cristal text-[rgb(var(--marco-tinta)/0.68)] hover:text-[rgb(var(--marco-tinta)/0.92)]"
              }`}
            >
              {p.icono !== undefined && (
                <p.icono size={16} aria-hidden={true} className="shrink-0" />
              )}
              {p.etiqueta}
            </button>
          )
        })}
      </div>
    </div>
  )
}
